import React, { useCallback, useEffect, useRef } from 'react'
import './edit-purchase.scss'
import { Row, Column } from '@/components/layout/layout'
import { ColumnsType } from 'antd/lib/table'
import { IPurchaseList } from './edit-purchase'
import useBemClass from '@/utils/hooks/useBEMClass'
import Iconfont from '@/components/iconfont/iconfont'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { layerManage } from '@/utils/layer'
import Button from '@/components/button/button'
import { Table } from 'antd'
import { purchaseOrderStore } from '../../model'
import $msg from '@/utils/message'
import { useRequest } from 'ahooks'
import { $post } from '@/api'
import classnames from 'classnames'

interface EditPruchaseConfirmProps {
    list: IPurchaseList[]
    orderId?: number
    eta: string
    confirm: boolean
}

const getInvalidDraftGoodsNames = (list: IPurchaseList[]) => {
    return list.filter(item => Number(item.subtotal) > 0 && !Number(item.amount)).map(item => item.goods.name)
}

const EditPruchaseConfirm: React.FC<EditPruchaseConfirmProps> = props => {
    const cls = useBemClass('edit-purchase')
    const submitInFlightRef = useRef(false)

    const columns: ColumnsType<IPurchaseList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'name',
            width: 120,
            dataIndex: 'goods',
            render: val => <div className="two-line">{val.name}</div>,
        },
        {
            title: '上架',
            width: 70,
            dataIndex: 'goods',
            render: val => (
                <Row>
                    {val.status === 0 || val.on_mall ? (
                        <React.Fragment>
                            <span className={cls('icon')}>
                                {val.on_mall && <Iconfont name="icon2shangjiaweishangcheng" size={20} />}
                            </span>
                            <span>
                                {val.status === 0 && (
                                    <Iconfont
                                        name="icon2shangjiashouyintai"
                                        size={20}
                                        style={{
                                            marginLeft: '8px',
                                        }}
                                    />
                                )}
                            </span>
                        </React.Fragment>
                    ) : (
                        <span>
                            <Iconfont name="iconweishangjia1" size={35}></Iconfont>
                        </span>
                    )}
                </Row>
            ),
        },
        {
            title: '供货商',
            key: 'supplier',
            width: 90,
            dataIndex: 'supplier',
            render: val => <div className="two-line">{val.name}</div>,
        },
        {
            title: '采购单价',
            key: 'price',
            dataIndex: 'price',
            width: 150,
            render: (v, r) => `${v}元/${r.purchase_unit?.name}`,
        },
        {
            title: '采购数量',
            key: 'amount',
            width: 130,
            dataIndex: 'amount',
            render: (v, r) => `${v}${r.purchase_unit?.name}`,
        },
        {
            title: '小计',
            key: 'subtotal',
            dataIndex: 'subtotal',
            align: 'right',
            width: 130,
            render: v => `${v}元`,
        },
        {
            title: '备注',
            key: 'remarks',
            dataIndex: 'remarks',
            align: 'right',
            width: 140,
            render: (val, record, index) => (
                <div className={classnames(cls('remark-txt'), 'two-line')}>{val || '-'}</div>
            ),
        },
    ]

    const createPurchaseOrder = useRequest(
        (list: any[]) =>
            $post({
                url: '/warehouse/createpurchaseorder',
                params: {
                    confirm: props.confirm,
                    replaced_order_id: props.orderId,
                    eta: props.eta,
                    purchase_order_goods_list: list,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                submitInFlightRef.current = false
                layerManage.popAll()
                $msg('success', `${props.orderId ? '编辑' : '创建'}成功`)
                purchaseOrderStore.setUpdateStatus()
            },
            onError: e => {
                submitInFlightRef.current = false
                $msg('error', e.message)
            },
        },
    )

    const handleSubmit = useCallback(() => {
        if (submitInFlightRef.current || createPurchaseOrder.loading) {
            return
        }

        if (!props.confirm) {
            const invalidDraftGoodsNames = getInvalidDraftGoodsNames(props.list)
            if (invalidDraftGoodsNames.length > 0) {
                $msg('error', `${invalidDraftGoodsNames.join('、')}未填写采购量，请补充采购量或清空小计金额`, 4)

                return
            }
        }

        const formatPurchaseList = props.list.map(list => ({
            amount: list.amount || 0,
            goods_id: list.goods.id,
            goods_name: list.goods.name,
            price: list.price || 0,
            purchase_unit_id: list.purchase_unit.id,
            purchase_unit_ratio: list.purchase_unit_ratio,
            purchase_unit_positive: list.purchase_unit_positive,
            remarks: list.remarks,
            subtotal: list.subtotal,
            supplier_id: list.supplier?.id,
            supplier_name: list.supplier?.name,
        }))

        submitInFlightRef.current = true
        createPurchaseOrder.run(formatPurchaseList)
    }, [createPurchaseOrder, props.confirm, props.list])

    useEffect(() => {
        if (!props.confirm) {
            return
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Enter') {
                return
            }

            event.preventDefault()
            event.stopPropagation()

            if (event.repeat || event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
                return
            }

            handleSubmit()
        }

        window.addEventListener('keydown', handleKeyDown, true)

        return () => window.removeEventListener('keydown', handleKeyDown, true)
    }, [handleSubmit, props.confirm])

    return (
        <BaseDialog
            isShow
            title="确认采购单"
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Row data-padding-16>
                <Column className={cls('preview')}>
                    <div className={cls('preview', 'title')}>采购清单({props.list.length})</div>
                    <Table
                        rowKey={record => record.goods.id}
                        dataSource={props.list}
                        columns={columns}
                        bordered
                        pagination={false}
                        scroll={{ y: '300px' }}
                    />
                </Column>
            </Row>
            <Row
                data-margin-top-10
                data-align-items-center
                data-justify-content-space-between
                data-margin-bottom-16
                data-margin-h-16
            >
                <div data-color-999 data-font-size-14>
                    *请仔细核对采购信息，确认清单后不可撤销
                </div>
                <Button types="default" loading={createPurchaseOrder.loading} onClick={handleSubmit}>
                    确定
                </Button>
            </Row>
        </BaseDialog>
    )
}

export default EditPruchaseConfirm
